import React, { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import HubSpotEmbeddedForm from "@/components/HubSpotEmbeddedForm";

const HUBSPOT_FORM_ID = "c56f1529-b1e6-48b2-8e43-132bbcfd26b5";

const HUBSPOT_TYPE_CLIENT_VALUES: Record<string, string> = {
  "Organisme de formation": "Organisme de formation",
  "École / CFA / Université": "École / CFA / Université",
  "Agence digitale": "Agence digitale",
  Entreprise: "Client",
  Autre: "Autre",
  // Compatibilité avec les anciens libellés du diagnostic et les brouillons en cours.
  "Direction formation grande entreprise": "Client",
  "Agence digital learning": "Agence digitale",
  "École ou université": "École / CFA / Université",
  "Autre acteur L&D": "Autre",
};

type HubSpotField = {
  name: string;
  value: string | string[] | boolean | number | null | undefined;
};

interface ContactData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  sector?: string;
  function: string;
  phone?: string;
  receiveReport?: boolean;
  orgType?: string;
  teamSize?: string;
}

interface AssessmentContactProps {
  contactInfo: ContactData;
  isSubmitting: boolean;
  onContactChange: (field: string, value: string | boolean) => void;
  onSubmit: (contactData: ContactData) => void;
  assessmentType: string;
  overallScore: number;
  organizationType?: string;
}

function normalizeFieldName(name: string) {
  return name.split("/").pop()?.toLowerCase() ?? name.toLowerCase();
}

function normalizeForSearch(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function valueAsString(value: HubSpotField["value"]) {
  if (Array.isArray(value)) return value.join(", ");
  if (value === null || value === undefined) return "";
  return String(value);
}

function getHubSpotMaturityValue(score: number) {
  if (score >= 70) return "Avancé";
  if (score >= 40) return "En structuration";
  return "Émergent";
}

function getHubSpotTypeClientValue(organizationType?: string) {
  if (!organizationType) return "";
  return HUBSPOT_TYPE_CLIENT_VALUES[organizationType] ?? "";
}

const AssessmentContact: React.FC<AssessmentContactProps> = ({
  onSubmit,
  overallScore,
  organizationType,
}) => {
  useEffect(() => {
    let cancelled = false;

    const handleFormReady = async (event: Event) => {
      const customEvent = event as CustomEvent<{ formId?: string }>;
      if (customEvent.detail?.formId !== HUBSPOT_FORM_ID) return;

      try {
        const hubspot = (window as any).HubSpotFormsV4;
        const form = hubspot?.getFormFromEvent?.(event);
        if (!form) return;

        const rawFields: HubSpotField[] = await form.getFormFieldValues();
        const maturityField = rawFields.find((field) => {
          const fieldName = normalizeForSearch(normalizeFieldName(field.name));
          return field.name.startsWith("0-2/") && fieldName.includes("maturit") && fieldName.includes("ia");
        });
        const typeClientField = rawFields.find(
          (field) => normalizeFieldName(field.name) === "typologie_partenaire",
        );

        if (!maturityField) {
          console.error("[Assessment] HubSpot company maturity field not found in the form.");
        } else {
          try {
            const maturityValue = getHubSpotMaturityValue(overallScore);
            await Promise.resolve(form.setFieldValue(maturityField.name, [maturityValue]));
          } catch (error) {
            console.error("[Assessment] HubSpot maturity value could not be injected.", error);
          }
        }

        const typeClientValue = getHubSpotTypeClientValue(organizationType);
        if (organizationType && !typeClientValue) {
          console.error(`[Assessment] No HubSpot Type Client mapping for diagnostic value: ${organizationType}`);
        } else if (typeClientValue) {
          if (!typeClientField) {
            console.error("[Assessment] HubSpot Type Client field typologie_partenaire not found in the form.");
          } else {
            try {
              await Promise.resolve(form.setFieldValue(typeClientField.name, [typeClientValue]));
            } catch (error) {
              console.error("[Assessment] HubSpot Type Client value could not be injected.", error);
            }
          }
        }
      } catch (error) {
        console.error("[Assessment] HubSpot hidden fields could not be prepared.", error);
      }
    };

    const handleSubmissionSuccess = async (event: Event) => {
      const customEvent = event as CustomEvent<{ formId?: string }>;
      if (customEvent.detail?.formId !== HUBSPOT_FORM_ID) return;

      try {
        const hubspot = (window as any).HubSpotFormsV4;
        const form = hubspot?.getFormFromEvent?.(event);
        const rawFields: HubSpotField[] = form ? await form.getFormFieldValues() : [];
        const fields = Object.fromEntries(
          rawFields.map((field) => [normalizeFieldName(field.name), valueAsString(field.value)]),
        );

        const contactData: ContactData = {
          firstName: fields.firstname || "",
          lastName: fields.lastname || "",
          email: fields.email || "",
          company: fields.company || "",
          sector: fields.industry || "",
          function: fields.jobtitle || "",
          phone: fields.phone || fields.mobilephone || "",
          receiveReport: true,
          orgType: organizationType,
        };

        if (!contactData.email || !contactData.firstName || !contactData.lastName || !contactData.company) {
          console.error("[Assessment] Missing required HubSpot contact fields after submission.");
          return;
        }

        if (!cancelled) onSubmit(contactData);
      } catch (error) {
        console.error("[Assessment] HubSpot submission could not be processed.", error);
      }
    };

    window.addEventListener("hs-form-event:on-ready", handleFormReady);
    window.addEventListener("hs-form-event:on-submission:success", handleSubmissionSuccess);

    return () => {
      cancelled = true;
      window.removeEventListener("hs-form-event:on-ready", handleFormReady);
      window.removeEventListener("hs-form-event:on-submission:success", handleSubmissionSuccess);
    };
  }, [onSubmit, organizationType, overallScore]);

  const formStyles = {
    "--hsf-global__font-family": "Nunito, Arial, sans-serif",
    "--hsf-global__font-size": "14px",
    "--hsf-global__color": "#111827",
    "--hsf-global-error__color": "#dc2626",
    "--hsf-button__font-family": "Nunito, Arial, sans-serif",
    "--hsf-button__font-size": "15px",
    "--hsf-button__color": "#ffffff",
    "--hsf-button__background-image": "linear-gradient(135deg, #D7398B, #FF6600)",
    "--hsf-button__border-radius": "10px",
    "--hsf-button__padding": "12px 20px",
    "--hsf-field-label__font-family": "Nunito, Arial, sans-serif",
    "--hsf-field-label__color": "#111827",
    "--hsf-field-label-requiredindicator__color": "#D7398B",
    "--hsf-field-input__font-family": "Nunito, Arial, sans-serif",
    "--hsf-field-input__background-color": "#ffffff",
    "--hsf-field-input__border-color": "#d1d5db",
    "--hsf-field-input__border-width": "1px",
    "--hsf-field-input__border-style": "solid",
    "--hsf-field-input__border-radius": "8px",
    "--hsf-field-input__padding": "10px 12px",
    "--hsf-background__background-color": "transparent",
    "--hsf-background__border-width": "0px",
    "--hsf-background__padding": "0px",
  } as React.CSSProperties;

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader className="text-center pb-3">
        <CardTitle className="text-2xl font-bold">
          <span className="bg-gradient-to-r from-[#D7398B] to-[#FF6600] bg-clip-text text-transparent">
            Vos résultats sont prêts
          </span>
        </CardTitle>
        <p className="text-gray-600 mt-2 max-w-xl mx-auto">
          Renseignez vos coordonnées pour accéder à votre diagnostic personnalisé et recevoir votre rapport par email.
          Vos réponses restent utilisées uniquement pour générer votre analyse ; vos coordonnées ainsi que des données
          de qualification synthétiques, comme votre type d’organisation et votre niveau de maturité, sont transmises au CRM de Nokodia.
        </p>
      </CardHeader>

      <CardContent className="p-6 pt-2 px-[23px]">
        <div style={formStyles}>
          <HubSpotEmbeddedForm
            formId={HUBSPOT_FORM_ID}
            title="Formulaire de réception du diagnostic de maturité"
            minHeight={820}
          />
        </div>

        <p className="text-xs text-gray-500 text-center mt-4">
          Les informations renseignées sont utilisées par Nokodia pour générer et vous transmettre votre diagnostic
          personnalisé. Pour en savoir plus sur le traitement de vos données et vos droits, consultez la{" "}
          <a href="/politique-confidentialite" className="underline">
            politique de confidentialité
          </a>
          .
        </p>
      </CardContent>
    </Card>
  );
};

export default AssessmentContact;
